/**
 * `<EvaluationCardsSkeleton>` — chrome-skeleton for the AI Evaluation tab
 * `loading` phase (Batch 30j-3).
 *
 * Composes `<Skeleton>` (foundations §9.19, introduced in 30j-2). Mirrors
 * the layout of the post-load `<EvaluationCards>` so the skeleton →
 * loaded transition is positionally stable.
 *
 * Hard rule: NO new shimmer code, NO new keyframes. The `<Skeleton>`
 * primitive is the single source of truth for shimmer styling.
 */
import type { JSX } from "react";

import { Skeleton } from "@/components/common/Skeleton";

export function EvaluationCardsSkeleton(): JSX.Element {
  return (
    <div
      className="space-y-3 px-content-px py-5"
      data-testid="ai-eval-skeleton"
    >
      {/* Recommendation card skeleton — verdict-tinted slot. */}
      <div className="rounded-xl border border-border-default bg-surface-card p-4">
        <div className="mb-3 flex items-center gap-3">
          <Skeleton width={140} height={11} rounded="md" />
          <Skeleton width={72} height={20} rounded="md" />
        </div>
        <Skeleton width="100%" height={12} className="mb-2" />
        <Skeleton width="92%" height={12} className="mb-2" />
        <Skeleton width="78%" height={12} />
      </div>

      {/* Detailed Assessment card skeleton — split-panel-shaped. */}
      <div className="rounded-xl border border-border-default bg-surface-card p-4">
        <Skeleton
          width={140}
          height={11}
          rounded="md"
          className="mb-3"
        />
        <div className="flex gap-0" style={{ height: "420px" }}>
          <div className="flex w-[200px] flex-col gap-2 border-r border-border-default p-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} width="100%" height={28} rounded="md" />
            ))}
          </div>
          <div className="flex-1 space-y-2 p-4">
            <Skeleton
              width={120}
              height={11}
              rounded="md"
              className="mb-3"
            />
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton
                key={i}
                width={`${85 + ((i * 13) % 15)}%`}
                height={11}
                rounded="md"
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
